import { Phone, Globe, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import useUserInfo from "@/hooks/use-user-info";

type ChatbotServiceProviderProps = {
  serviceProvider?: string;
  website?: string;
  phone?: string;
  email?: string;
};

const ChatbotServiceProvider: React.FC<ChatbotServiceProviderProps> = (
  props,
) => {
  const {
    serviceProvider: chatbotServiceProvider,
    serviceProviderWebsite: chatbotServiceProviderWebsite,
    displayWebsite: chatbotDisplayWebsite,
    serviceProviderPhoneNumber: chatbotServiceProviderPhoneNumber,
    serviceProviderEmail: chatbotServiceProviderEmail,
    serviceDisplayEmail: chatbotServiceDisplayEmail,
  } = useUserInfo()?.chatbotDetails || {};

  const hasAnyValidPropValue = Object.values(props).some(
    (value) => typeof value === "string" && value.trim().length > 0,
  );

  const providerDetails: ChatbotServiceProviderProps = hasAnyValidPropValue
    ? {
        serviceProvider: props?.serviceProvider,
        website: props?.website,
        phone: props?.phone,
        email: props?.email,
      }
    : {
        serviceProvider: chatbotServiceProvider,
        website: chatbotServiceProviderWebsite || chatbotDisplayWebsite,
        phone: chatbotServiceProviderPhoneNumber,
        email: chatbotServiceProviderEmail || chatbotServiceDisplayEmail,
      };

  const handleCallNow = () => {
    window.location.href = `tel:${providerDetails?.phone || ""}`;
  };

  return (
    <div className="border-border/20 mx-auto max-w-sm rounded-2xl border bg-card p-4 shadow-card">
      {/* Avatar and Name */}
      <div className="mb-3 flex items-center gap-2">
        <div className="flex size-12 items-center justify-center rounded-full bg-orange-600 shadow-sm">
          <span className="text-lg font-bold text-white">
            {providerDetails?.serviceProvider?.charAt(0)?.toUpperCase() || ""}
          </span>
        </div>
        <h2 className="text-lg font-semibold text-gray-600">
          {providerDetails?.serviceProvider || ""}
        </h2>
      </div>
      {/* Contact Information */}
      <div className="mb-4 space-y-1">
        {Boolean(providerDetails?.email) && (
          <div className="flex items-center gap-3">
            <Mail className="size-5 text-muted-foreground" />
            <span className="text-medium text-muted-foreground">Email:</span>
            <Button
              href={`mailto:${providerDetails?.email}`}
              target="_blank"
              className="hover:text-primary/80 w-[14.75rem] truncate text-medium font-medium no-underline transition-colors hover:underline"
            >
              {providerDetails?.email}
            </Button>
          </div>
        )}
        {Boolean(providerDetails?.website) && (
          <div className="flex items-center gap-3">
            <Globe className="size-5 text-muted-foreground" />
            <span className="text-medium text-muted-foreground">Website:</span>
            <a
              href={providerDetails?.website}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary/80 w-[14.75rem] truncate text-medium font-medium no-underline transition-colors hover:underline"
            >
              {providerDetails?.website}
            </a>
          </div>
        )}
        {Boolean(providerDetails?.phone) && (
          <div className="flex items-center gap-3">
            <Phone className="size-5 text-muted-foreground" />
            <span className="text-medium text-muted-foreground">Phone:</span>
            <span className="text-medium font-medium text-gray-700">
              {providerDetails?.phone}
            </span>
          </div>
        )}
      </div>
      {/* Call Button */}
      {Boolean(providerDetails?.phone) && (
        <Button
          onClick={handleCallNow}
          className="hover:bg-primary/90 shadow-contact w-full rounded-lg bg-primary px-6 py-3 font-medium text-primary-foreground transition-all duration-200 hover:shadow-lg"
        >
          <Phone className="mr-2 size-4" />
          Call Now
        </Button>
      )}
    </div>
  );
};

export default ChatbotServiceProvider;
